import { Tag } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CategoryResponse } from '@/api/client';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface CategorySelectorProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	categories: CategoryResponse[];
	currentCategory?: string;
	onSelect: (categoryId: string) => void;
}

export function CategorySelector({
	open,
	onOpenChange,
	categories,
	currentCategory,
	onSelect,
}: CategorySelectorProps) {
	const [query, setQuery] = useState('');
	const [selectedIndex, setSelectedIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	// Filter categories by search query
	const filteredCategories = useMemo(() => {
		if (!query.trim()) return categories;
		const lowerQuery = query.toLowerCase();
		return categories.filter(
			(cat) =>
				cat.name.toLowerCase().includes(lowerQuery) || cat.id.toLowerCase().includes(lowerQuery)
		);
	}, [categories, query]);

	// Focus input when dialog opens
	useEffect(() => {
		if (open) {
			setTimeout(() => inputRef.current?.focus(), 0);
			// Pre-select current category if exists
			const currentIndex = categories.findIndex((c) => c.id === currentCategory);
			setSelectedIndex(currentIndex >= 0 ? currentIndex : 0);
		} else {
			setQuery('');
			setSelectedIndex(0);
		}
	}, [open, categories, currentCategory]);

	// Reset selection when filter changes
	useEffect(() => {
		setSelectedIndex(0);
	}, []);

	// Keyboard navigation
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) => Math.min(prev + 1, filteredCategories.length - 1));
					break;
				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex((prev) => Math.max(prev - 1, 0));
					break;
				case 'Enter':
					e.preventDefault();
					if (filteredCategories[selectedIndex]) {
						onSelect(filteredCategories[selectedIndex].id);
						onOpenChange(false);
					}
					break;
				case 'Escape':
					onOpenChange(false);
					break;
			}
		},
		[filteredCategories, selectedIndex, onSelect, onOpenChange]
	);

	const handleSelect = useCallback(
		(categoryId: string) => {
			onSelect(categoryId);
			onOpenChange(false);
		},
		[onSelect, onOpenChange]
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="top-[20%] translate-y-0 p-0 gap-0 overflow-hidden max-w-md"
			>
				{/* Search Input */}
				<div className="flex items-center border-b px-3">
					<Tag className="size-4 text-muted-foreground shrink-0" />
					<input
						ref={inputRef}
						type="text"
						placeholder="Search categories..."
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={handleKeyDown}
						className="flex-1 h-12 px-3 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
					/>
					<kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 sm:flex">
						ESC
					</kbd>
				</div>

				{/* Category List */}
				<ScrollArea className="max-h-[300px]">
					{filteredCategories.length === 0 && query.trim() && (
						<div className="py-6 text-center text-sm text-muted-foreground">
							No categories found.
						</div>
					)}
					{filteredCategories.length > 0 && (
						<div className="p-2">
							{filteredCategories.map((category, index) => (
								<button
									key={category.id}
									type="button"
									onClick={() => handleSelect(category.id)}
									className={cn(
										'w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors',
										index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
										category.id === currentCategory && 'ring-1 ring-primary/50'
									)}
								>
									<span
										className="size-4 rounded-full shrink-0"
										style={{ backgroundColor: category.color }}
									/>
									{category.icon && <span className="text-base">{category.icon}</span>}
									<span className="flex-1 font-medium text-sm">{category.name}</span>
									{category.id === currentCategory && (
										<span className="text-xs text-muted-foreground">current</span>
									)}
								</button>
							))}
						</div>
					)}
				</ScrollArea>

				{/* Footer */}
				<div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
					<div className="flex items-center gap-2">
						<kbd className="px-1.5 py-0.5 rounded border bg-muted font-mono">↑↓</kbd>
						<span>Navigate</span>
						<kbd className="px-1.5 py-0.5 rounded border bg-muted font-mono">↵</kbd>
						<span>Select</span>
					</div>
					<div>
						{filteredCategories.length} categor{filteredCategories.length !== 1 ? 'ies' : 'y'}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
